import { useEffect, useState } from 'react';
import cx from 'classnames';

import getCardIcon from 'util/getCardIcon';

import Currency from 'components/Currency/Currency';
import AppleButton from 'components/Button/AppleButton';
import AddCard from 'components/SavedPaymentMethods/AddCard';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import useCheckoutStore from 'stores/CheckoutStoreV2';
import GoogleButton from 'components/Button/GoogleButton';
import PaypalButton from 'components/Button/PaypalButton';
import VenmoButton from 'components/Button/VenmoButton';
import useWalletStore from 'stores/WalletStoreV2';
import SubmitButton from './SubmitButton';
import { ReactComponent as IconTrash } from 'assets/images/icons/close/close.svg';
import ConfirmationPopover from 'components/Popover/ConfirmationPopover';

const PaymentMethodOption = ({ card, isActive, onClick, uiStore }) => {
  const walletStore = useWalletStore();
  const checkoutStore = useCheckoutStore();
  const containerClassName = cx(
    'font-agenda flex flex-1 justify-between px-2.5 py-[15px] rounded-[10px] capitalize',
    {
      'bg-lightgray-e8e': isActive,
      '!text-gray-400 cursor-not-allowed': card.isDisabled,
      'hover:bg-lightgray-e8e cursor-pointer': !card.isDisabled,
    },
  );

  return (
    <div className="flex justify-between">
      <div
        className={containerClassName}
        onClick={card.isDisabled ? undefined : onClick}
      >
        <div className="text-sm h-[14px] my-auto">
          <span>{card.name || card.brand}</span>
          {card.isDisabled && (
            <span className="text-red-400 ml-4">
              {card.stripeCardId.includes('card_') ? 'UNAVAILABLE' : 'EXPIRED'}
            </span>
          )}
        </div>
        <div className="flex gap-x-[5px] text-sm flex-1 justify-end">
          {card.brand && <img src={getCardIcon(card.brand)} />}
          {card.last4 && (
            <>
              <div className="h-4 my-auto">****</div>
              <div className="h-4 my-auto">{card.last4}</div>
            </>
          )}
          {card.balance && (
            <>
              <div className="flex h-4 my-auto gap-x-1">
                <span className="text-gray-595">Available Balance</span>
                <Currency showCents amount={card.balance} />
              </div>
            </>
          )}
        </div>
      </div>
      {['CARD', 'ACH'].includes(card.accountType) && (
        <ConfirmationPopover
          message="Are you sure you want to remove this payment method?"
          onConfirm={async () => {
            await walletStore.removeCard({
              cardId: card.id,
              userContext: checkoutStore.activeEntity?.userContext,
              silent: true,
            });
          }}
          className="w-5 my-auto p-0 bg-transparent border-none cursor-pointer"
        >
          <IconTrash />
        </ConfirmationPopover>
      )}
    </div>
  );
};

const PaymentMethodsContent = ({ isMobile, onAddCard, authStore, uiStore }) => {
  const elements = useElements();
  const stripe = useStripe();
  const checkoutStore = useCheckoutStore();
  const walletStore = useWalletStore();
  const { FRAME } = checkoutStore;

  const [selectedCard, setSelectedCard] = useState(
    checkoutStore.activePaymentMethod,
  );
  const [showAddCard, setShowAddCard] = useState(false);

  const handleConfirm = async (card) => {
    checkoutStore.update({ activePaymentMethod: card });
    await checkoutStore.updateCheckout();
    checkoutStore.setFrame(isMobile ? FRAME.CHECKOUT_SUMMARY : FRAME.CHECKOUT);
  };

  useEffect(() => {
    setSelectedCard(checkoutStore.activePaymentMethod);
  }, [checkoutStore.activePaymentMethod]);

  return (
    <div className="px-5 flex flex-col h-full">
      <div className="flex justify-center w-full pb-2.5">
        <div className="font-agenda-bold text-[20px] mx-auto py-2">
          Payment Method
        </div>
      </div>
      <div
        className={cx('overflow-y-auto overflow-x-hidden', {
          'flex-1 pb-2.5 mb-2.5': walletStore.cards?.length,
        })}
      >
        {walletStore.balance >= checkoutStore.splitAmount?.total &&
          checkoutStore.giftType?.toUpperCase() !== 'SELF_GIFT' && (
            <PaymentMethodOption
              card={{
                name: 'Cauze Balance',
                balance: walletStore.balance,
              }}
              isActive={selectedCard?.paymentType === 'BALANCE'}
              onClick={() => {
                setSelectedCard({
                  paymentType: 'BALANCE',
                });
              }}
            />
          )}
        {walletStore.cards.map((card) => (
          <PaymentMethodOption
            key={card.id}
            card={card}
            isActive={selectedCard?.id === card.id}
            onClick={() => {
              setSelectedCard(card);
            }}
            uiStore={uiStore}
          />
        ))}
      </div>
      {window.ApplePaySession ? (
        <AppleButton
          className="mb-1.5"
          onClick={() => {
            handleConfirm({ paymentType: 'APPLE_PAY' });
          }}
        />
      ) : (
        <GoogleButton
          className="mb-1.5"
          onClick={() => {
            handleConfirm({ paymentType: 'GOOGLE_PAY' });
          }}
        />
      )}
      <div className="flex flex-col gap-y-1.5 mb-1.5">
        <PaypalButton
          onClick={() => {
            checkoutStore.setActivePaymentMethod({
              paymentType: 'paypal',
            });

            checkoutStore.setFrame(
              isMobile ? FRAME.CHECKOUT_SUMMARY : FRAME.CHECKOUT,
            );
          }}
        />
        <VenmoButton
          onClick={() => {
            checkoutStore.setActivePaymentMethod({
              paymentType: 'paypal',
            });

            checkoutStore.setFrame(
              isMobile ? FRAME.CHECKOUT_SUMMARY : FRAME.CHECKOUT,
            );
          }}
        />
      </div>
      <div className="mb-2">
        {isMobile ? (
          <button
            className="cz-btn-outline black w-full !border-gray-949"
            onClick={() => {
              checkoutStore.setFrame(FRAME.ADD_NEW_CARD);
            }}
          >
            {authStore.isAuthenticated ? 'Add New Card' : 'CREDIT CARD / ACH'}
          </button>
        ) : (
          <AddCard
            showAddCard={showAddCard}
            setShowAddCard={setShowAddCard}
            isCheckout
            onAddCard={() => {
              checkoutStore.setFrame(FRAME.ADD_NEW_CARD);
            }}
          />
        )}
      </div>
      <div className="mt-auto">
        <SubmitButton
          disabled={!selectedCard && !showAddCard}
          onSubmit={async () => {
            let card = selectedCard;

            if (showAddCard) {
              card = await onAddCard({ elements, stripe });
              checkoutStore.update({ activePaymentMethod: card });
              setSelectedCard(card);
              setShowAddCard(false);
            }

            handleConfirm(card);
          }}
        />
      </div>
    </div>
  );
};

export default PaymentMethodsContent;
